import {
  Elements,
  PaymentElement,
  useElements,
  useStripe
} from '@stripe/react-stripe-js';
import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { PAYMENT_CHECKOUT_SESSION_ID } from 'dpl/constants/query_params';
import PaymentMethodOption from 'dpl/buyer_account/components/PaymentMethodOption';
import {
  buyerPaymentMethodQueryDefinition,
  chargeSavedPaymentMethodV2Mutation
} from 'dpl/buyer_account/utils/resourceDefinitions';
import { GDButtonV1 } from 'dpl/common/core/GDButton/GDButtonV1';
import LoadingWrapper from 'dpl/components/LoadingWrapper';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEWS,
  ANALYTICS_VIEW_FEATURES
} from 'dpl/constants/analytics';
import useResourceMutation from 'dpl/shared/fetching/hooks/useResourceMutation';
import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import useToastNotifications from 'dpl/shared/hooks/useToastNotifications';
import { isNativeAppUA } from 'dpl/shared/utils';
import { sendRudderstackEvent } from 'dpl/util/analytics';
import { stripePromise } from 'dpl/util/stripePromise';
import { trackPaymentConversion } from 'dpl/breeder_dashboard/payments/utils/analytics';

import StripeChargeModalFooter from './StripeChargeModalFooter';
import { SUBMIT_PAYMENT_LABEL } from './constants';

const IS_NATIVE_APP = isNativeAppUA();

function PrivateFormComponent({
  analytics,
  children,
  className,
  disabled,
  externalId,
  id,
  onChange, // Fires onChange and onReady for StripePaymentElement if no default payment is set
  onSubmit,
  isFetching,
  breederProfileId,
  buyerProfileId,
  paymentCategory,
  paymentAmount,
  paymentMethod
}) {
  const elements = useElements();
  const stripe = useStripe();
  const { pushToastNotification } = useToastNotifications();

  const checkoutSessionId = useRef(uuidv4());

  const forceNewMethod = useSelector(
    ({ queryParams }) => queryParams.force_new_method === 'true'
  );

  const [isDefaultPaymentSelected, setIsDefaultPaymentSelected] =
    useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submissionPageUrl = new URL(
    `/pay/${externalId}/submission?${PAYMENT_CHECKOUT_SESSION_ID}=${checkoutSessionId.current}`,
    window.location.origin
  ).href;

  const successPageUrl = new URL(
    `/pay/${externalId}/success?${PAYMENT_CHECKOUT_SESSION_ID}=${checkoutSessionId.current}`,
    window.location.origin
  ).href;

  const failurePageUrl = new URL(
    `/pay/${externalId}/failure?${PAYMENT_CHECKOUT_SESSION_ID}=${checkoutSessionId.current}`,
    window.location.origin
  ).href;

  const { data, isFetching: isDefaultPaymentMethodFetching } = useResourceQuery(
    buyerPaymentMethodQueryDefinition()
  );
  const defaultPaymentMethod = data?.data?.stripe_payment_method;

  // * When the form is submitting, we could not find a way to make the form elements not clickable. // * So to prevent the customer from changing stuff part way through submitting, we just don't send // * `onChange` events. On submit fail, this can cause a de-sync between the UI and the actual payment // * request record.
  function handleChange(stripeData) {
    if (!isSubmitting) {
      onChange(stripeData);
    }
  }

  useEffect(() => {
    if (forceNewMethod) {
      return;
    }
    setIsDefaultPaymentSelected(!!defaultPaymentMethod);
  }, [defaultPaymentMethod]);

  useEffect(() => {
    if (isDefaultPaymentSelected) {
      handleChange({ value: { type: defaultPaymentMethod.type } });
    }
  }, [isDefaultPaymentSelected]);

  useEffect(() => {
    sendRudderstackEvent(ANALYTICS_EVENTS.PAYMENT_CHECKOUT_STARTED_BY_BUYER, {
      view: analytics.view || ANALYTICS_VIEWS.PAYMENT_DETAILS,
      view_feature:
        analytics.viewFeature || ANALYTICS_VIEW_FEATURES.PAYMENT_CHECKOUT_PAGE,
      payment_request_id: id,
      breeder_profile_id: breederProfileId,
      buyer_profile_id: buyerProfileId,
      payment_type: paymentCategory,
      checkout_session_id: checkoutSessionId.current
    });
  }, []);

  const { mutateAsync: chargeSavedPaymentMethodAsync } = useResourceMutation(
    chargeSavedPaymentMethodV2Mutation(id)
  );

  async function chargeSavedPaymentMethod() {
    try {
      const {
        data: {
          status,
          payment_intent: {
            status: paymentIntentStatus,
            client_secret: clientSecret
          }
        }
      } = await chargeSavedPaymentMethodAsync();

      if (status === 'ok') {
        if (paymentIntentStatus === 'requires_action') {
          const { error } = await stripe.confirmCardPayment(clientSecret, {
            return_url: successPageUrl
          });

          if (error) {
            pushToastNotification({ message: error.message, type: 'error' });
            sendRudderstackEvent(
              ANALYTICS_EVENTS.PAYMENT_REQUEST_FAILED_BY_BUYER,
              {
                view: analytics.view || ANALYTICS_VIEWS.PAYMENT_DETAILS,
                view_feature:
                  analytics.viewFeature ||
                  ANALYTICS_VIEW_FEATURES.PAYMENT_CHECKOUT_PAGE,
                buyer_profile_id: buyerProfileId,
                breeder_profile_id: breederProfileId,
                payment_request_id: id,
                payment_type: paymentCategory,
                checkout_session_id: checkoutSessionId.current,
                failure_reason: error.message
              }
            );

            setIsSubmitting(false);

            return;
          }
        }

        await trackPaymentConversion(paymentCategory, paymentAmount);
        window.location.assign(successPageUrl);
      } else if (status === 'balance_check_pending') {
        // try again after giving the balance refresh time to complete
        setTimeout(chargeSavedPaymentMethod, 2000);
      } else if (status === 'balance_check_failed') {
        window.location.assign(failurePageUrl);
      } else {
        // TODO: notify bugsnag here about unhandled status
        pushToastNotification({
          type: 'error',
          message: 'There was a problem charging your saved payment method.'
        });
        setIsSubmitting(false);
      }
    } catch {
      pushToastNotification({
        type: 'error',
        message: 'There was a problem charging your saved payment method.'
      });
      setIsSubmitting(false);
    }
  }

  async function handleSubmit(e) {
    try {
      e.preventDefault();

      setIsSubmitting(true);

      sendRudderstackEvent(
        ANALYTICS_EVENTS.PAYMENT_CHECKOUT_SUBMITTED_BY_BUYER,
        {
          view: analytics.view || ANALYTICS_VIEWS.PAYMENT_DETAILS,
          view_feature:
            analytics.viewFeature ||
            ANALYTICS_VIEW_FEATURES.PAYMENT_CHECKOUT_PAGE,
          payment_request_id: id,
          breeder_profile_id: breederProfileId,
          buyer_profile_id: buyerProfileId,
          payment_type: paymentCategory,
          checkout_session_id: checkoutSessionId.current
        }
      );

      onSubmit?.();

      if (isDefaultPaymentSelected) {
        chargeSavedPaymentMethod();
      } else {
        const { error } = await stripe.confirmSetup({
          confirmParams: { return_url: submissionPageUrl },
          elements
        });

        if (error && error.type !== 'validation_error') {
          pushToastNotification({
            type: 'error',
            message: error.message
          });
          setIsSubmitting(false);
        }
      }
    } catch {
      pushToastNotification({
        type: 'error',
        message: 'There was a problem charging your saved payment method.'
      });
    }
  }

  function handleOpenInBrowser() {
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({
        type: 'OpenInBrowser',
        value: window.location.href
      })
    );
  }

  const shouldDisplayPaymentMethodOptions =
    !forceNewMethod && Boolean(defaultPaymentMethod);

  return (
    <form className={className}>
      <LoadingWrapper isLoading={isDefaultPaymentMethodFetching}>
        {shouldDisplayPaymentMethodOptions && (
          <section>
            <h2 className="lh-title f2 mt8 mb4 font-sans">
              Select payment method
            </h2>
            <PaymentMethodOption
              active={isDefaultPaymentSelected}
              paymentMethod={defaultPaymentMethod}
              onClick={() => {
                setIsDefaultPaymentSelected(true);
              }}
            />
            <PaymentMethodOption
              className="mt2"
              active={!isDefaultPaymentSelected}
              onClick={() => {
                setIsDefaultPaymentSelected(false);
              }}
            />
          </section>
        )}
        {!isDefaultPaymentSelected && (
          <section>
            <h2 className="lh-title f2 mt8 mb4 font-sans">
              {IS_NATIVE_APP && 'Payment method'}
              {!IS_NATIVE_APP && shouldDisplayPaymentMethodOptions
                ? 'Add new payment method'
                : 'Select payment method'}
            </h2>
            <PaymentElement
              options={{
                readOnly: isFetching || isSubmitting,
                terms: {
                  applePay: 'never',
                  auBecsDebit: 'never',
                  bancontact: 'never',
                  card: 'never',
                  cashapp: 'never',
                  googlePay: 'never',
                  ideal: 'never',
                  paypal: 'never',
                  sepaDebit: 'never',
                  sofort: 'never',
                  usBankAccount: 'never'
                }
              }}
              onChange={handleChange}
            />
            {IS_NATIVE_APP && (
              <p className="f1 mt4">
                To pay by bank transfer (ACH) please{' '}
                <button
                  type="button"
                  className="link underline primary"
                  onClick={handleOpenInBrowser}
                >
                  visit our website
                </button>
              </p>
            )}
          </section>
        )}
        {children}
        <div className="pt2">
          <GDButtonV1
            disabled={disabled || isSubmitting || isFetching}
            type="submit"
            onClick={handleSubmit}
            className="PayNowButton w-100"
          >
            {SUBMIT_PAYMENT_LABEL}
          </GDButtonV1>
          <StripeChargeModalFooter paymentMethod={paymentMethod} />
        </div>
      </LoadingWrapper>
    </form>
  );
}

PrivateFormComponent.propTypes = {
  analytics: PropTypes.shape({
    view: PropTypes.string,
    viewFeature: PropTypes.string
  }),
  children: PropTypes.node,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  isFetching: PropTypes.bool.isRequired,
  externalId: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  onSubmit: PropTypes.func,
  paymentCategory: PropTypes.string.isRequired,
  paymentAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired
};

PrivateFormComponent.defaultProps = {
  analytics: {},
  children: null,
  disabled: false,
  className: '',
  onChange: null,
  onSubmit: null
};

export function CompletePaymentRequestFormV3({ clientSecret, ...args }) {
  const options = {
    clientSecret,
    appearance: {
      variables: {
        spacingUnit: '6px',
        borderRadius: '8px'
      }
    }
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      <PrivateFormComponent {...args} />
    </Elements>
  );
}

CompletePaymentRequestFormV3.propTypes = {
  ...PrivateFormComponent.propTypes,
  clientSecret: PropTypes.string.isRequired
};

CompletePaymentRequestFormV3.defaultProps = {
  ...PrivateFormComponent.defaultProps
};
